import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Send, Mail, Phone, MapPin, CheckCircle2, Building2 } from "lucide-react";

const PROJECT_TYPES = [
  "Thermal Power Plant",
  "Alumina Refinery",
  "Steel Plant",
  "Cement Plant",
  "Oil & Gas / Petrochemical",
  "EPC Construction",
  "Shutdown / Turnaround",
  "Other",
];

const MANPOWER_CATEGORIES = [
  "welder", "fitter", "electrician", "plumber",
  "rigger", "technician", "helper", "mason",
];

const CAT_LABEL = {
  welder: "Welder", fitter: "Fitter", electrician: "Electrician", plumber: "Plumber",
  rigger: "Rigger", technician: "Technician", helper: "Helper", mason: "Mason",
};

export default function EnquiryForm() {
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    project_type: PROJECT_TYPES[0], manpower_categories: [],
    headcount: "", location: "", start_date: "", message: "",
    website: "", // honeypot — bots will fill this, humans won't
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const toggleCat = (c) =>
    setForm((s) => ({
      ...s,
      manpower_categories: s.manpower_categories.includes(c)
        ? s.manpower_categories.filter((x) => x !== c)
        : [...s.manpower_categories, c],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email || !form.phone) {
      return toast.error("Please fill Name, Company, Email and Phone");
    }
    setBusy(true);
    try {
      await api.post("/enquiries", {
        ...form,
        headcount: form.headcount ? Number(form.headcount) : null,
      });
      setDone(true);
      toast.success("Enquiry received — we'll be in touch within 24 hours.");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="sarthi-card p-8 text-center" data-testid="enquiry-success">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
          <CheckCircle2 className="h-9 w-9 text-green-700" strokeWidth={2.5} />
        </div>
        <h3 className="font-display text-2xl font-extrabold text-sarthi-text">Enquiry sent!</h3>
        <p className="mt-2 text-base font-medium text-sarthi-muted">
          Thank you for reaching out. A Sarthi representative will contact you within 24 hours to discuss your manpower requirements.
        </p>
        <button
          data-testid="enquiry-send-another"
          onClick={() => { setDone(false); setForm({ name: "", company: "", email: "", phone: "", project_type: PROJECT_TYPES[0], manpower_categories: [], headcount: "", location: "", start_date: "", message: "", website: "" }); }}
          className="sarthi-btn-outline mt-6 px-5 py-2 text-sm"
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="sarthi-card p-6 sm:p-8" data-testid="enquiry-form">
      {/* Honeypot — invisible to humans; bots typically fill it. */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
        <label htmlFor="website">Website</label>
        <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field testid="enq-name" label="Your Name *" value={form.name} onChange={set("name")} />
        <Field testid="enq-company" label="Company Name *" value={form.company} onChange={set("company")} icon={<Building2 className="h-4 w-4" />} />
        <Field testid="enq-email" label="Work Email *" type="email" value={form.email} onChange={set("email")} icon={<Mail className="h-4 w-4" />} />
        <Field testid="enq-phone" label="Phone Number *" value={form.phone} onChange={set("phone")} inputMode="tel" icon={<Phone className="h-4 w-4" />} />
      </div>

      <div className="mt-4">
        <label className="mb-1 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">Project Type</label>
        <select
          data-testid="enq-project-type"
          value={form.project_type}
          onChange={set("project_type")}
          className="w-full rounded-xl border-2 border-sarthi-border bg-white px-4 py-3 text-base font-semibold focus:border-sarthi-primary focus:outline-none"
        >
          {PROJECT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      <div className="mt-4">
        <label className="mb-2 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">Manpower Needed</label>
        <div className="flex flex-wrap gap-2">
          {MANPOWER_CATEGORIES.map((c) => {
            const sel = form.manpower_categories.includes(c);
            return (
              <button
                type="button"
                key={c}
                data-testid={`enq-cat-${c}`}
                onClick={() => toggleCat(c)}
                className={`rounded-full border-2 px-4 py-2 text-sm font-bold ${sel ? "border-sarthi-primary bg-sarthi-accent text-sarthi-primary" : "border-sarthi-border bg-white text-sarthi-text"}`}
              >
                {CAT_LABEL[c]}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field testid="enq-headcount" label="Estimated Headcount" type="number" value={form.headcount} onChange={set("headcount")} />
        <Field testid="enq-location" label="Site Location" value={form.location} onChange={set("location")} icon={<MapPin className="h-4 w-4" />} />
        <Field testid="enq-start-date" label="Expected Start Date" type="date" value={form.start_date} onChange={set("start_date")} />
      </div>

      <div className="mt-4">
        <label className="mb-1 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">Message / Scope of Work</label>
        <textarea
          data-testid="enq-message"
          value={form.message}
          onChange={set("message")}
          rows={4}
          placeholder="Briefly describe your project, key skills required, contract duration, safety compliance needs…"
          className="w-full rounded-xl border-2 border-sarthi-border bg-white px-4 py-3 text-base font-semibold focus:border-sarthi-primary focus:outline-none"
        />
      </div>

      <button
        type="submit"
        data-testid="enq-submit"
        disabled={busy}
        className="sarthi-btn-primary mt-6 flex w-full items-center justify-center gap-2 py-4 text-lg text-white disabled:opacity-50"
      >
        <Send className="h-5 w-5" /> {busy ? "Sending…" : "Send Enquiry"}
      </button>
      <p className="mt-3 text-center text-xs font-semibold text-sarthi-muted">
        Response guaranteed within 24 hours · Your details are confidential
      </p>
    </form>
  );
}

function Field({ label, value, onChange, type = "text", inputMode, testid, icon }) {
  return (
    <div>
      <label className="mb-1 flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-sarthi-muted">
        {icon} {label}
      </label>
      <input
        data-testid={testid}
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={onChange}
        className="w-full rounded-xl border-2 border-sarthi-border bg-white px-4 py-3 text-base font-semibold focus:border-sarthi-primary focus:outline-none"
      />
    </div>
  );
}
